import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Building2, 
  Hammer, 
  HardHat, 
  Clock, 
  Award, 
  Users, 
  CheckCircle2,
  ArrowDown,
  Phone,
  Mail,
  Cpu,
  Zap,
  Shield,
  Eye,
  Smartphone,
  Wifi,
  Camera,
  BarChart3,
  Globe,
  Layers
} from "lucide-react";
import AppointmentModal from "./AppointmentModal";

const backgroundImages = [
  "/assets/in1.jpg",
  "/assets/in2.jpg",
  "/assets/in3.jpg",
  "/assets/in4.jpg",
  "/assets/in5.jpg",
  "/assets/in6.jpg",
  // Add more images to the slideshow
];


const features = [
  {
    icon: Building2,
    title: "Premium Construction",
    description: "Urbanwall's signature quality in every project we undertake",
  },
  {
    icon: Hammer,
    title: "Master Craftsmanship",
    description: "Elite team of skilled professionals with proven expertise",
  },
  {
    icon: HardHat,
    title: "Safety Excellence",
    description: "Industry-leading safety standards and protocols",
  },
  {
    icon: Clock,
    title: "Timely Completion",
    description: "Precise project management ensuring on-time delivery",
  },
];

const techFeatures = [
  {
    icon: Cpu,
    title: "Smart Building Systems",
    description: "IoT-enabled construction with real-time monitoring and automation",
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    textColor: "text-blue-600"
  },
  {
    icon: Camera,
    title: "3D Modeling & BIM",
    description: "Advanced Building Information Modeling for precise project visualization",
    color: "from-purple-500 to-purple-600",
    bgColor: "bg-purple-50",
    textColor: "text-purple-600"
  },
  {
    icon: BarChart3,
    title: "Project Analytics",
    description: "Data-driven insights for optimized construction processes and cost management",
    color: "from-green-500 to-green-600",
    bgColor: "bg-green-50",
    textColor: "text-green-600"
  },
  {
    icon: Smartphone,
    title: "Mobile Management",
    description: "Real-time project updates and communication through our mobile platform",
    color: "from-orange-500 to-orange-600",
    bgColor: "bg-orange-50",
    textColor: "text-orange-600"
  },
  {
    icon: Shield,
    title: "Digital Security",
    description: "Advanced cybersecurity measures protecting your project data and communications",
    color: "from-red-500 to-red-600",
    bgColor: "bg-red-50",
    textColor: "text-red-600"
  },
  {
    icon: Globe,
    title: "Cloud Integration",
    description: "Seamless cloud-based collaboration and document management systems",
    color: "from-indigo-500 to-indigo-600",
    bgColor: "bg-indigo-50",
    textColor: "text-indigo-600"
  }
];

const stats = [
  { number: "25+", label: "Projects Delivered", prefix: "" },
  { number: "5", label: "Years of Excellence", prefix: "+" },
  { number: "100", label: "Client Satisfaction", prefix: "%" },
  { number: "15", label: "Expert Team Members", prefix: "+" },
];

const achievements = [
  {
    icon: Award,
    title: "Best Construction Company ",
    description: "Recognized for excellence in sustainable building practices",
  },
  {
    icon: Users,
    title: "Customer Trust",
    description: "Highest customer satisfaction rating in the industry",
  },
  {
    icon: CheckCircle2,
    title: "Quality Certified",
    description: "ISO 9001:2015 certified for quality management",
  },
];

const Home = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prevImage) => (prevImage + 1) % backgroundImages.length);
    }, 5000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const scrollToProjects = () => {
    const galleryElement = document.getElementById('gallery');
    if (galleryElement) {
      galleryElement.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
    } else {
      console.warn('Gallery element not found');
    }
  };

  return (
    <>
      <div className="min-h-screen">
        {/* Modern Hero Section */}
        <header id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
          {/* Background Image with better overlay */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentImage}
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url(${backgroundImages[currentImage]})`,
              }}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5 }}
            />
          </AnimatePresence>

          {/* Modern gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-blue-900/80 to-emerald-900/60" />

          {/* Subtle Floating Elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {/* Minimal floating elements for subtle animation */}
            <motion.div
              className="absolute top-1/4 left-1/4 w-2 h-2 bg-cyan-400/20 rounded-full"
              animate={{ 
                y: [0, -20, 0],
                opacity: [0.2, 0.5, 0.2]
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <motion.div
              className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-400/30 rounded-full"
              animate={{ 
                y: [0, 15, 0],
                opacity: [0.3, 0.6, 0.3]
              }}
              transition={{ 
                duration: 8,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 2
              }}
            />
            <motion.div
              className="absolute bottom-1/3 left-1/3 w-1.5 h-1.5 bg-emerald-400/25 rounded-full"
              animate={{ 
                y: [0, -12, 0],
                opacity: [0.25, 0.5, 0.25]
              }}
              transition={{ 
                duration: 7,
                repeat: Infinity,
                ease: "easeInOut",
                delay: 4
              }}
            />
          </div>

          {/* Clean Contact Info */}
          <div className="absolute top-20 right-3 sm:top-24 sm:right-6 z-20">
            <div className="flex flex-col gap-2 sm:gap-3">
              <a href="tel:+918740074320" 
                className="flex items-center gap-2 sm:gap-3 text-white/80 hover:text-white transition-colors 
                  text-xs sm:text-sm group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center 
                  group-hover:bg-white/20 transition-colors">
                  <Phone className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <span className="font-medium hidden sm:inline">+91 8740074320</span>
                <span className="font-medium sm:hidden">Call</span>
              </a>
              <a href="mailto:info@urbanewal.in" 
                className="flex items-center gap-2 sm:gap-3 text-white/80 hover:text-white transition-colors 
                  text-xs sm:text-sm group"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center 
                  group-hover:bg-white/20 transition-colors">
                  <Mail className="w-3 h-3 sm:w-4 sm:h-4" />
                </div>
                <span className="font-medium hidden sm:inline">info@urbanewal.in</span>
                <span className="font-medium sm:hidden">Email</span>
              </a>
            </div>
          </div>

          {/* Clean Main Content */}
          <div className="relative z-10 flex flex-col justify-center items-center text-center container mx-auto 
            px-4 sm:px-6 md:px-8 lg:px-12 pt-20 pb-20"
          >
            <div className="max-w-5xl">
              {/* Clean Company Name */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-6 sm:mb-8"
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold 
                  tracking-tight text-white leading-tight"
                >
                  <span className="bg-gradient-to-r from-cyan-400 to-blue-500 
                    bg-clip-text text-transparent">URBAN</span>
                  <span className="text-white">WALL</span>
                </h1>
                <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 mt-2 sm:mt-3 md:mt-4 font-light">
                  Construction & Development
                </p>
              </motion.div>

              {/* Clean tagline */}
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.8 }}
                className="text-base sm:text-lg md:text-xl lg:text-2xl xl:text-3xl font-light text-white/90 
                  leading-relaxed mb-4 sm:mb-6 md:mb-8 lg:mb-12 max-w-3xl"
              >
                Building Tomorrow&apos;s Landmarks Today
              </motion.h2>

              {/* Clean CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.8 }}
                className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center items-center"
              >
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-white text-slate-900 rounded-lg text-base sm:text-lg font-semibold 
                    transition-all duration-300 hover:bg-slate-100 hover:scale-105 shadow-lg w-full max-w-xs sm:w-auto"
                >
                    Get Free Consultation
                </button>

                <button
                  onClick={scrollToProjects}
                  className="px-6 sm:px-8 py-3 sm:py-4 bg-transparent border-2 border-white text-white rounded-lg text-base sm:text-lg font-semibold 
                    transition-all duration-300 hover:bg-white hover:text-slate-900 hover:scale-105 w-full max-w-xs sm:w-auto"
                >
                  View Projects
                </button>
              </motion.div>

              {/* Clean Stats Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8 }}
                className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8"
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">
                      {stat.number}{stat.prefix}
                    </div>
                    <div className="text-xs sm:text-sm text-white/70 font-medium leading-tight">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Clean Scroll Indicator */}
          <motion.div
            className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-20"
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2 }}
          >
            <div className="flex flex-col items-center gap-2">
              <span className="text-white/60 text-xs font-medium tracking-wider uppercase">
                Scroll Down
              </span>
              <ArrowDown className="w-5 h-5 text-white/60" />
            </div>
          </motion.div>
        </header>

        {/* Clean Features Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-slate-900">
                Why Choose Us
              </h2>
              <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto">
                We combine expertise, innovation, and dedication to deliver exceptional 
                construction solutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 
                    group-hover:bg-slate-200 transition-colors duration-300">
                    <feature.icon className="w-7 h-7 sm:w-8 sm:h-8 text-slate-700" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-slate-900">{feature.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Clean Technology Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-slate-900">
          <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
                Smart Construction Technology
              </h2>
              <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto">
                We integrate cutting-edge technology to deliver smarter, 
                more efficient, and sustainable building solutions.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {techFeatures.map((tech, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="text-center group"
                >
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 
                    group-hover:bg-white/20 transition-colors duration-300">
                    <tech.icon className="w-7 h-7 sm:w-8 sm:h-8 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-4">
                    {tech.title}
                  </h3>
                  <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                    {tech.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Clean Tech Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="mt-12 sm:mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8"
            >
              {[
                { icon: Eye, label: "Real-time Monitoring", value: "24/7" },
                { icon: Wifi, label: "IoT Devices", value: "50+" },
                { icon: Layers, label: "BIM Models", value: "25+" },
                { icon: Zap, label: "Energy Efficiency", value: "40%" }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">
                    {stat.value}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-300 leading-tight">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Clean Achievements Section */}
        <section className="py-12 sm:py-16 md:py-20 bg-slate-50">
          <div className="container mx-auto px-4 sm:px-6 md:px-8 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12 sm:mb-16"
            >
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-slate-900">Our Achievements</h2>
              <p className="text-lg sm:text-xl text-slate-600 max-w-3xl mx-auto">
                Recognition of our commitment to excellence and innovation in construction.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {achievements.map((achievement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  className="bg-white p-6 sm:p-8 rounded-2xl shadow-sm text-center group hover:shadow-md transition-shadow duration-300"
                >
                  <achievement.icon className="w-10 h-10 sm:w-12 sm:h-12 text-slate-700 mb-4 sm:mb-6 mx-auto" />
                  <h3 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-slate-900">{achievement.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{achievement.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>

      <AppointmentModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
      />
    </>
  );
};

export default Home;
